<template>
  <v-row no-gutters class="contracts-dashboard">
    <v-col v-if="userService.hasRight('c_create_customer')" cols="12" class="current-membership">
      <div v-if="membershipLoading" class="current-membership__loading">
        <v-progress-circular indeterminate color="primary" :size="42" :width="4" />
        <div>
          <strong>Aktuelle Mitgliedschaft wird geladen</strong>
          <span>TAC-Vertrag und Pakete werden direkt abgerufen.</span>
        </div>
      </div>

      <template v-else-if="currentTacMembership">
        <div class="current-membership__main">
          <span class="current-membership__icon">
            <v-icon>mdi-card-account-details-star-outline</v-icon>
          </span>
          <div class="current-membership__identity">
            <span class="current-membership__eyebrow">AKTUELLER TAC-VERTRAG</span>
            <h3>{{ currentTacMembership.NAME }}</h3>
            <div class="current-membership__meta">
              <span>
                <v-icon x-small>mdi-calendar-range-outline</v-icon>
                {{ currentTacPeriod }}
              </span>
              <span v-if="currentTacMembership.TERM_TYPE">
                <v-icon x-small>mdi-file-document-outline</v-icon>
                {{ currentTacMembership.TERM_TYPE }}
              </span>
              <span v-if="currentTacCenter">
                <v-icon x-small>mdi-map-marker-outline</v-icon>
                {{ currentTacCenter }}
              </span>
            </div>
            <p v-if="currentTacExtensions.length" class="current-membership__total">
              Gültig bis {{ $moment(currentTacEnd).format("DD.MM.YYYY") }} inkl. {{ currentTacExtensionLabel }}
            </p>
          </div>
          <div class="current-membership__status">
            <strong>{{ currentTacDaysRemaining }}</strong>
            <span>{{ currentTacDaysRemaining === 1 ? "Tag verbleibend" : "Tage verbleibend" }}</span>
          </div>
          <v-btn color="primary" depressed class="text-none font-weight-bold current-membership__upgrade" @click="$emit('open-upgrade')">
            <v-icon left>mdi-trending-up</v-icon>
            Upgrade lösen
          </v-btn>
        </div>

        <div class="current-membership__details">
          <div v-if="currentTacExtensions.length" class="current-membership__extensions">
            <span class="current-membership__detail-label">BONUSZEIT &amp; VERLÄNGERUNGEN</span>
            <ul>
              <li v-for="(period, index) in currentTacExtensions" :key="index">
                <strong>{{ period.TERM_TYPE === "Ruhezeitverlängerung" ? "Ruhezeitverlängerung" : "Bonuszeit" }}</strong>
                <span>{{ $moment(period.TERM_STARTDATE).format("DD.MM.YYYY") }} – {{ $moment(period.TERM_ENDDATE).format("DD.MM.YYYY") }}</span>
              </li>
            </ul>
          </div>
          <div class="current-membership__packages">
            <span class="current-membership__detail-label">AKTIVE PAKETE &amp; ZUSATZLEISTUNGEN</span>
            <div v-if="currentTacPackages.length" class="current-membership__package-list">
              <span
                v-for="contractPackage in currentTacPackages"
                :key="contractPackage.CONTRACTID || contractPackage.CONTRACTIDSEQUENCEID || contractPackage.NAME"
                class="current-membership__package">
                <v-icon x-small>mdi-check</v-icon>
                {{ contractPackage.NAME }}
              </span>
            </div>
            <span v-else class="current-membership__empty-package">Keine zusätzlichen Pakete hinterlegt.</span>
          </div>

          <button v-if="pendingUpgrades.length" type="button" class="current-membership__pending" @click="$emit('open-upgrade')">
            <span>
              <v-icon>mdi-clock-alert-outline</v-icon>
            </span>
            <span>
              <strong>{{ pendingUpgrades.length }} {{ pendingUpgrades.length === 1 ? "offenes Upgrade" : "offene Upgrades" }}</strong>
              <small>Details öffnen</small>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </button>
        </div>
      </template>

      <div v-else class="current-membership__empty">
        <span class="current-membership__icon current-membership__icon--muted">
          <v-icon>mdi-card-off-outline</v-icon>
        </span>
        <div>
          <span class="current-membership__eyebrow">AKTUELLER TAC-VERTRAG</span>
          <h3>Keine laufende Mitgliedschaft gefunden</h3>
          <p>Abgelaufene TAC-Verträge und bestehende App-Verträge bleiben unten verfügbar.</p>
        </div>
      </div>
    </v-col>

    <v-col v-if="premiumContract" cols="12" class="premium-guests">
      <div class="premium-guests__header">
        <div>
          <span class="premium-guests__eyebrow">PREMIUM-VORTEIL</span>
          <h3>Gäste mitnehmen</h3>
          <p>
            {{ filteredGuestEntries.length }} von 10 Eintritten im aktuellen Vertragsjahr
            <span v-if="premiumGuestPeriod">· {{ premiumGuestPeriodLabel }}</span>
          </p>
        </div>
        <v-icon>mdi-account-multiple-plus-outline</v-icon>
      </div>
      <v-row no-gutters class="premium-guests__slots">
        <v-col v-for="n in maxGuestEntries" :key="'guestentries' + n" cols="auto">
          <v-chip
            @click="handleGuestSlot(n)"
            class="mr-2"
            :class="{ 'font-weight-bold clickable': n <= filteredGuestEntries.length + 1 }"
            :disabled="n > filteredGuestEntries.length + 1"
            :color="n > filteredGuestEntries.length ? 'grey' : 'primary'">
            {{ filteredGuestEntries.length === n - 1 ? "+" : n }}
          </v-chip>
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <v-row no-gutters>
        <v-col align="center" cols="12">
          <v-tabs class="contract-tabs mt-3" v-model="tab" centered dark>
            <v-tab v-for="(tab, index) of tabs" :key="index">{{ tab.text }}</v-tab>
          </v-tabs>
        </v-col>
      </v-row>

      <v-row v-if="userService.hasRight('c_create_customer')" no-gutters>
        <v-col cols="12">
          <v-tabs-items dark v-model="tab">
            <v-tab-item>
              <v-row no-gutters>
                <v-col class="px-5 mt-5" cols="12" v-if="!contracts || contracts.length === 0">
                  <v-alert type="info">Keine Verträge gefunden</v-alert>
                </v-col>
                <v-col v-for="(contract, index) of contracts" :key="index + '-roodyContract'" :cols="isMobile ? '12' : '6'">
                  <v-card class="contract-card contract-card--roody mt-7 mx-4 fill-height" color="grey darken-4">
                    <header class="contract-card__header">
                      <div class="contract-card__heading">
                        <span class="contract-card__eyebrow">APP-VERTRAG</span>
                        <h3>{{ getContractTitle(contract) }}</h3>
                        <span class="contract-card__category">{{ getContractCategory(contract) }}</span>
                      </div>
                      <v-chip small class="contract-status-chip" :color="mapContractState(contract.status).color">
                        {{ mapContractState(contract.status).text }}
                      </v-chip>
                    </header>

                    <div class="contract-card__meta">
                      <div>
                        <v-icon small>mdi-calendar-range-outline</v-icon>
                        <span>
                          <small>Vertragslaufzeit</small>
                          <strong>
                            {{ $moment(contract.startDate).format("DD.MM.YYYY") }} – {{ $moment(contract.endDate).format("DD.MM.YYYY") }}
                          </strong>
                        </span>
                      </div>
                      <div v-if="contract.createdAt">
                        <v-icon small>mdi-clock-outline</v-icon>
                        <span>
                          <small>Erstellt am</small>
                          <strong>{{ $moment(contract.createdAt).format("DD.MM.YYYY") }}</strong>
                        </span>
                      </div>
                    </div>

                    <div v-if="contract.hasPurchasedPersonalTraining" class="contract-card__notice">
                      <v-icon small>mdi-information-outline</v-icon>
                      Personal Training gekauft
                    </div>

                    <div class="contract-card__packages">
                      <span class="contract-card__section-label">ZUSATZPAKETE</span>
                      <div v-if="contract.packages && contract.packages.length" class="contract-card__package-list">
                        <v-chip v-for="(contractPackage, packageIndex) of contract.packages" :key="packageIndex + '-roodyPackage'" small outlined>
                          {{ contractPackage.title }}
                        </v-chip>
                      </div>
                      <span v-else class="contract-card__empty">Keine Zusatzpakete hinterlegt.</span>
                    </div>

                    <v-card-actions class="contract-card__actions">
                      <span class="contract-card__section-label">AKTIONEN</span>
                      <div class="contract-card__action-list">
                        <v-btn
                          small
                          depressed
                          color="primary"
                          class="text-none"
                          title="Vollständigen Vertrag ansehen"
                          @click="$router.push('/dashboard/contract/view/' + user._id + '/' + index)">
                          <v-icon left small>mdi-eye-outline</v-icon>
                          Ansehen
                        </v-btn>
                        <v-btn
                          small
                          outlined
                          color="info"
                          class="text-none"
                          title="Interne Kommentare ansehen"
                          @click="openContractComments(contract)">
                          <v-icon left small>mdi-comment-text-multiple-outline</v-icon>
                          Kommentare
                          <span class="contract-action__count">{{ getCommentCount(contract) }}</span>
                        </v-btn>
                        <v-btn small outlined color="grey lighten-1" class="text-none" title="Vertrag als PDF drucken" @click="printUserContract(index)">
                          <v-icon left small>mdi-printer-outline</v-icon>
                          PDF drucken
                        </v-btn>
                        <v-btn
                          v-if="contract.status !== 'accepted'"
                          small
                          outlined
                          color="grey lighten-1"
                          class="text-none"
                          title="Zahlungsbedingungen ändern"
                          @click="showPaymentDialog = index + '-contractPaymentIndex'">
                          <v-icon left small>mdi-credit-card-settings-outline</v-icon>
                          Zahlung ändern
                        </v-btn>
                        <v-btn
                          v-if="contract.status !== 'accepted'"
                          small
                          outlined
                          color="grey lighten-1"
                          class="text-none"
                          title="Vertragsanhänge verwalten"
                          @click="showAttachmentDialog = user._id + '-' + index">
                          <v-icon left small>mdi-file-image-plus</v-icon>
                          Anhänge
                        </v-btn>
                        <v-btn
                          v-if="contract.status !== 'accepted'"
                          small
                          outlined
                          color="grey lighten-1"
                          class="text-none"
                          title="Vertrag bearbeiten"
                          @click="showEditDialog = index + '-contractEditIndex'">
                          <v-icon left small>mdi-pencil-outline</v-icon>
                          Bearbeiten
                        </v-btn>
                        <v-btn
                          v-if="userService.hasRight('c_create') && (contract.status === 'accepted' || contract.status === 'deleted')"
                          small
                          outlined
                          color="error"
                          class="text-none"
                          title="Vertrag ablehnen"
                          @click="declineContract(index)">
                          <v-icon left small>mdi-block-helper</v-icon>
                          Ablehnen
                        </v-btn>
                      </div>
                    </v-card-actions>
                  </v-card>

                  <basic-dialog
                    ref="paymentDialog"
                    dialogTitle="Zahlungsbedingungen"
                    @onCancel="showPaymentDialog = null"
                    :dialogVisible="showPaymentDialog === index + '-contractPaymentIndex'"
                    @onSave="editPaymentContract(index, contract.paymentOptions)"
                    :dialogSaveButtonText="'Speichern'"
                    dark>
                    <v-row no-gutters class="pa-4">
                      <v-col class="d-flex justify-center mb-5" v-if="!tempContract" cols="12">
                        <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                      </v-col>

                      <v-col v-else cols="12" class="mb-2">
                        <div class="text-subtitle-1 grey--text text--lighten-1 mb-4">Bitte wähle eine Zahlungsoption:</div>
                        <v-row no-gutters>
                          <v-col
                            cols="12"
                            v-for="(paymentOption, i) of tempContract.formFields
                              .find((e) => e.inputType === 'paymentOptions')
                              .inputValue.filter((e) => e.studios.includes(contract.studio._id))"
                            :key="i + '-indexPaymentOptions'"
                            class="mb-3">
                            <v-card
                              hover
                              rounded="lg"
                              :color="
                                contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title
                                  ? 'primary'
                                  : 'grey darken-3'
                              "
                              :class="{
                                'elevation-4': contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title,
                              }"
                              @click="
                                contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title
                                  ? (contract.paymentOptions = {
                                      selection: null,
                                      selectedDate: null,
                                      additionalInfo: contract.paymentOptions.additionalInfo || '',
                                    })
                                  : (contract.paymentOptions = {
                                      selection: paymentOption,
                                      selectedDate: null,
                                      additionalInfo: contract.paymentOptions.additionalInfo || '',
                                    })
                              "
                              class="transition-swing clickable-card">
                              <v-card-text class="d-flex align-center py-4 px-5">
                                <v-icon
                                  :color="
                                    contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title
                                      ? 'white'
                                      : 'grey darken-1'
                                  "
                                  class="mr-4"
                                  large>
                                  {{
                                    contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title
                                      ? "mdi-check-circle"
                                      : "mdi-circle-outline"
                                  }}
                                </v-icon>
                                <span
                                  class="text-body-1 font-weight-medium"
                                  :class="
                                    contract.paymentOptions.selection && contract.paymentOptions.selection.title === paymentOption.title
                                      ? 'white--text'
                                      : 'grey--text text--lighten-2'
                                  ">
                                  {{ paymentOption.title }}
                                </span>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col class="mb-2 mt-4" v-if="tempContract" cols="12">
                        <v-divider class="mb-6 border-opacity-25"></v-divider>
                        <v-textarea
                          outlined
                          rounded="lg"
                          background-color="grey darken-4"
                          label="Zusätzliche Informationen"
                          counter
                          rows="3"
                          :rules="[
                            (v) => (v || '').length <= 150 || 'Bitte nur maximal 150 Zeichen eingeben',
                            (v) => !v || v.indexOf('\n') < 0 || 'Bitte keine Zeilenumbrüche eingeben',
                          ]"
                          v-model="contract.paymentOptions.additionalInfo"></v-textarea>
                      </v-col>
                    </v-row>
                  </basic-dialog>

                  <basic-dialog
                    ref="editDialog"
                    dialogTitle="Vertrag bearbeiten"
                    @onCancel="showEditDialog = null"
                    :dialogVisible="showEditDialog === index + '-contractEditIndex'"
                    @onSave="$router.push('/dashboard/contract/edit/' + user._id + '/' + index)"
                    :dialogSaveButtonText="'Fortfahren'"
                    dark>
                    <v-row class="pa-4">
                      <v-col cols="12">
                        <v-alert type="warning" icon="mdi-alert-circle-outline" border="left" prominent rounded="lg" class="mb-6 grey darken-3">
                          Wenn du den Vertrag bearbeitest, musst du die Unterschrift des Kunden erneut einholen. Möchtest du wirklich fortfahren?
                        </v-alert>
                        <v-alert color="info" icon="mdi-information-outline" outlined rounded="lg" class="mb-0">
                          Anhänge kannst du über das
                          <v-icon color="info" small class="mx-1" @click="showEditDialog = null">mdi-file-image-plus</v-icon> Icon ohne extra
                          Unterschrift hinzufügen.
                        </v-alert>
                      </v-col>
                    </v-row>
                  </basic-dialog>

                  <basic-dialog
                    ref="attachmentDialog"
                    @onCancel="showAttachmentDialog = null"
                    @onSave="addFileToContract(index)"
                    dialogTitle="Anhänge verwalten"
                    :dialogVisible="showAttachmentDialog === user._id + '-' + index"
                    dark>
                    <v-row no-gutters class="pa-4">
                      <v-col cols="12">
                        <v-file-input
                          multiple
                          outlined
                          rounded="lg"
                          prepend-icon="mdi-camera-plus"
                          background-color="grey darken-3"
                          color="primary"
                          @change="uploadImagesContract($event, contract)"
                          label="Neue Bilder auswählen"></v-file-input>
                      </v-col>

                      <v-col cols="12" v-if="contract.customer && contract.customer.additionalImages && contract.customer.additionalImages.length">
                        <v-divider class="mb-4"></v-divider>
                        <v-window v-model="activeIndexContract" class="elevation-2 rounded-lg grey darken-4">
                          <v-window-item v-for="(image, imageIndex) in contract.customer.additionalImages" :key="`card-${imageIndex}`">
                            <v-card color="transparent" height="250" class="d-flex flex-column align-center justify-center position-relative">
                              <v-img
                                v-if="image.dataUrl"
                                contain
                                height="200px"
                                width="100%"
                                :src="image.dataUrl"
                                alt="Uploaded Image"
                                class="rounded-lg"></v-img>

                              <div v-else class="text-center">
                                <v-icon size="50" color="grey darken-2" class="mb-3">mdi-image-off-outline</v-icon><br />
                                <v-btn rounded outlined color="primary" @click="fetchImageDataSource(imageIndex, contract)">
                                  <v-icon left>mdi-cloud-download</v-icon> Bild laden
                                </v-btn>
                              </div>

                              <v-btn
                                fab
                                x-small
                                color="error"
                                class="absolute-top-right mt-2 mr-2"
                                @click="deleteImageContract(imageIndex, contract)"
                                elevation="4">
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </v-card>
                          </v-window-item>
                        </v-window>

                        <v-card-actions class="justify-center pt-4">
                          <v-item-group v-model="activeIndexContract" class="text-center" mandatory>
                            <v-item v-for="n in contract.customer.additionalImages.length" :key="`btn-${n}`" v-slot="{ active, toggle }">
                              <v-btn :color="active ? 'primary' : 'grey darken-2'" icon small @click="toggle">
                                <v-icon small>mdi-circle</v-icon>
                              </v-btn>
                            </v-item>
                          </v-item-group>
                        </v-card-actions>
                      </v-col>
                    </v-row>
                  </basic-dialog>
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <v-row no-gutters>
                <v-col cols="12" class="justify-center d-flex">
                  <v-switch v-model="tacContractsSwitch" label="Abgelaufene Verträge anzeigen"></v-switch>
                </v-col>
                <v-col cols="12" class="px-5 mt-5" v-if="!runningTACContracts.length > 0 && !oldTACContracts.length > 0">
                  <v-alert type="info">Keine Verträge gefunden</v-alert>
                </v-col>
                <v-col cols="12">
                  <v-row v-if="!tacContractsSwitch" no-gutters>
                    <v-col cols="12" class="d-flex justify-center px-5 mb-4">
                      <v-btn
                        v-if="userService.hasRight('c_open_contracts')"
                        color="error"
                        dark
                        rounded
                        depressed
                        @click="showTerminationWizard = true">
                        <v-icon left>mdi-file-cancel</v-icon>
                        Kündigungs-Assistent öffnen
                      </v-btn>
                    </v-col>
                    <v-col
                      v-for="(contract, index) of runningTACContracts.filter(
                        (e) => e.CONTRACTTYP === 'Hauptvertrag' || (e.CONTRACTTYP === 'Bonusvertrag' && e.GROUP_NAME !== 'Zusatzpakete'),
                      )"
                      :key="index + '-runningTACContract'"
                      :cols="isMobile ? '12' : '6'">
                      <v-card color="grey darken-4" align="center" class="contract-card mx-4 mb-7">
                        <v-card-title class="justify-center">{{ contract.NAME }}</v-card-title>
                        <v-card-subtitle v-if="contract.TERM_TYPE" class="justify-center pb-0">
                          {{ contract.TERM_TYPE }}
                        </v-card-subtitle>
                        <v-card-text class="pb-0">
                          <div>
                            {{ $moment(contract.TERM_STARTDATE).format("DD.MM.YYYY") }} -
                            {{ $moment(contract.TERM_ENDDATE).format("DD.MM.YYYY") }}
                          </div>
                        </v-card-text>
                        <v-card-text class="text-center">
                          <v-chip
                            v-for="(additionalContract, index2) of runningTACContracts.filter(
                              (e) => e.CONTRACTTYP === 'Zusatzvertrag' || (e.CONTRACTTYP === 'Bonusvertrag' && e.GROUP_NAME === 'Zusatzpakete'),
                            )"
                            v-show="
                              additionalContract.TERM_STARTDATE === contract.TERM_STARTDATE &&
                              additionalContract.TERM_ENDDATE === contract.TERM_ENDDATE
                            "
                            class="primary accent-4 white--text mx-1"
                            :key="index2 + '-runningTACAdditionalContract'"
                            title="Zusatzpaket kündigen">
                            {{ additionalContract.NAME }}
                          </v-chip>
                        </v-card-text>
                        <v-row v-if="userService.hasRight('d_c_healthcare')" no-gutters>
                          <v-col
                            cols="12"
                            v-for="year of $moment(contract.TERM_ENDDATE).diff($moment(contract.TERM_STARTDATE), 'years') + 1"
                            :key="year + 'contractYear'"
                            class="d-flex justify-center"
                            align="center">
                            <v-row no-gutters>
                              <v-col class="d-flex justify-center" cols="12">
                                <v-switch
                                  :label="year + ' Jahr ' + 'vollständig bezahlt'"
                                  @change="updateContractPayment($event, contract.CONTRACTIDSEQUENCEID, 0, year)"
                                  :input-value="
                                    user.paidTACContractIDs &&
                                    user.paidTACContractIDs.find(
                                      (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                    )
                                  "></v-switch>
                              </v-col>
                              <v-col
                                v-if="
                                  user.paidTACContractIDs &&
                                  user.paidTACContractIDs.find((e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year)
                                "
                                cols="12">
                                <v-text-field
                                  type="number"
                                  class="mx-5"
                                  :value="
                                    user.paidTACContractIDs &&
                                    user.paidTACContractIDs.find(
                                      (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                    )
                                      ? user.paidTACContractIDs.find(
                                          (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                        ).amount
                                      : 0
                                  "
                                  @blur="updateContractPayment(true, contract.CONTRACTIDSEQUENCEID, $event.target.value, year)"
                                  label="Kosten des Abos (KK-Bestätigung)"></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                        <v-spacer></v-spacer>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row v-else no-gutters>
                    <v-col
                      v-for="(contract, index) of oldTACContracts.filter(
                        (e) => e.CONTRACTTYP === 'Hauptvertrag' || (e.CONTRACTTYP === 'Bonusvertrag' && e.GROUP_NAME !== 'Zusatzpakete'),
                      )"
                      :key="index + '-runningTACContract'"
                      :cols="isMobile ? '12' : '6'">
                      <v-card color="grey darken-4" align="center" class="contract-card contract-card--expired mx-4 mb-7">
                        <v-card-title class="justify-center">
                          {{ contract.NAME }}
                          <br />
                          (abgelaufen)
                        </v-card-title>
                        <v-card-subtitle v-if="contract.TERM_TYPE" class="justify-center pb-0">
                          {{ contract.TERM_TYPE }}
                        </v-card-subtitle>
                        <v-card-text class="pb-0">
                          <div>
                            {{ $moment(contract.TERM_STARTDATE).format("DD.MM.YYYY") }} -
                            {{ $moment(contract.TERM_ENDDATE).format("DD.MM.YYYY") }}
                          </div>
                        </v-card-text>
                        <v-card-text class="text-center">
                          <v-chip
                            v-for="(additionalContract, index2) of oldTACContracts.filter(
                              (e) => e.CONTRACTTYP === 'Zusatzvertrag' || (e.CONTRACTTYP === 'Bonusvertrag' && e.GROUP_NAME === 'Zusatzpakete'),
                            )"
                            v-show="
                              additionalContract.TERM_STARTDATE === contract.TERM_STARTDATE &&
                              additionalContract.TERM_ENDDATE === contract.TERM_ENDDATE
                            "
                            class="primary accent-4 white--text mx-1"
                            :key="index2 + '-runningTACAdditionalContract'">
                            {{ additionalContract.NAME }}
                          </v-chip>
                        </v-card-text>
                        <v-row v-if="userService.hasRight('d_c_healthcare')" no-gutters>
                          <v-col
                            cols="12"
                            v-for="year of $moment(contract.TERM_ENDDATE).diff($moment(contract.TERM_STARTDATE), 'years') + 1"
                            :key="year + 'contractYear'"
                            class="d-flex justify-center"
                            align="center">
                            <v-row no-gutters>
                              <v-col class="d-flex justify-center" cols="12">
                                <v-switch
                                  :label="year + ' Jahr ' + 'vollständig bezahlt'"
                                  @change="updateContractPayment($event, contract.CONTRACTIDSEQUENCEID, 0, year)"
                                  :input-value="
                                    user.paidTACContractIDs &&
                                    user.paidTACContractIDs.find(
                                      (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                    )
                                  "></v-switch>
                              </v-col>
                              <v-col
                                v-if="
                                  user.paidTACContractIDs &&
                                  user.paidTACContractIDs.find((e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year)
                                "
                                cols="12">
                                <v-text-field
                                  type="number"
                                  class="mx-5"
                                  :value="
                                    user.paidTACContractIDs &&
                                    user.paidTACContractIDs.find(
                                      (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                    )
                                      ? user.paidTACContractIDs.find(
                                          (e) => e.id.toString() === contract.CONTRACTIDSEQUENCEID.toString() && e.year === year,
                                        ).amount
                                      : 0
                                  "
                                  @blur="updateContractPayment(true, contract.CONTRACTIDSEQUENCEID, $event.target.value, year)"
                                  label="Kosten des Abos (KK-Bestätigung)"></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col cols="12" class="px-5 mt-5">
          <v-alert type="info">Funktion nicht freigeschaltet</v-alert>
        </v-col>
      </v-row>
    </v-col>

    <v-dialog max-width="500" dark v-model="createNewGuestEntry" transition="dialog-bottom-transition">
      <v-card rounded="lg" color="grey darken-4" elevation="12">
        <v-card-title class="pa-5 border-bottom d-flex justify-space-between align-center">
          <span class="text-h6 font-weight-bold">Gast Eintritt</span>
          <v-chip color="primary" outlined small>
            {{ newGuestEntry.date.format("DD.MM.YYYY") }}
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <div class="text-body-2 grey--text text--lighten-1 mb-4">
            Suche nach einem bestehenden Gast. Neue Gäste erfasst du über das Gästebuch im Kalender.
          </div>
          <user-search
            :key="newGuestEntry.user"
            class="mx-auto"
            ignoreRights
            :value="newGuestEntry.user"
            label="Gast suchen..."
            outlined
            @update="setGuestUser($event)"></user-search>
        </v-card-text>

        <template v-if="newGuestEntry.user">
          <v-divider></v-divider>

          <v-card-actions class="pa-4 flex-wrap justify-center" style="gap: 10px">
            <v-btn
              v-if="existingDocs && existingDocs.medicalHistory && existingDocs.medicalHistory.length"
              :disabled="loadingExistingDocs"
              outlined
              rounded
              color="info"
              block
              class="mb-2"
              @click="viewGuestAnamnese()">
              <v-icon left>mdi-eye-outline</v-icon>
              Anamnese ansehen
            </v-btn>
            <div v-else-if="!loadingExistingDocs" class="text-body-2 grey--text text--lighten-1 text-center mb-2">
              Bitte zuerst eine Anamnese über einen Termin im Arbeitskalender erstellen.
            </div>

            <v-btn
              rounded
              block
              :disabled="!existingDocs || !existingDocs.medicalHistory || !existingDocs.medicalHistory.length"
              :loading="loadingExistingDocs"
              color="primary"
              elevation="4"
              @click="updateUserGuestEntries()">
              <v-icon left>mdi-content-save</v-icon> Eintritt speichern
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <anamnese-bogen-create ref="anamneseBogenRef"></anamnese-bogen-create>
    <v-dialog v-model="showTerminationWizard" max-width="600" persistent>
      <mail-termination-wizard
        v-if="showTerminationWizard"
        :user="wizardUserData"
        :emailDate="null"
        @cancel="showTerminationWizard = false"
        @completed="onTerminationCompleted" />
    </v-dialog>
    <v-dialog max-width="400" dark v-model="showPremiumDialog" transition="fade-transition">
      <v-card rounded="lg" color="grey darken-4" elevation="12" v-if="showExistingGuestEntry">
        <v-card-title class="pa-5 d-flex justify-space-between align-center">
          <span class="text-h6">Eintritt Details</span>
          <v-btn icon color="error" @click="deletePremiumGuest" title="Eintritt löschen">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center pt-2 pb-6">
          <v-icon size="40" color="primary" class="mb-3">mdi-calendar-check</v-icon>
          <div class="text-h5 white--text mb-1">{{ $moment(showExistingGuestEntry.date).format("DD.MM.YYYY") }}</div>
          <div class="grey--text">Erfasster Gast-Eintritt</div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 d-flex flex-column align-stretch" style="gap: 12px">
          <v-btn rounded color="primary" elevation="2" @click="$router.push('/dashboard/user/search/' + showExistingGuestEntry.user)">
            <v-icon left>mdi-account-arrow-right</v-icon> Profil öffnen
          </v-btn>
          <v-btn rounded text color="grey" @click="showExistingGuestEntry = null"> Schliessen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="Boolean(commentDialogContract)"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="680"
      scrollable
      content-class="contract-comments-dialog-shell"
      @input="closeContractComments">
      <v-card v-if="commentDialogContract" dark class="contract-comments-card">
        <header class="contract-comments-card__header">
          <div>
            <span class="contract-comments-card__eyebrow">VERTRAGSVERLAUF</span>
            <h3>Interne Kommentare</h3>
            <p>{{ getContractTitle(commentDialogContract) }}</p>
          </div>
          <v-btn icon aria-label="Kommentare schliessen" @click="closeContractComments(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="contract-comments-card__content">
          <div v-if="activeContractComments.length" class="contract-comments-list">
            <article v-for="(comment, commentIndex) in activeContractComments" :key="`${comment.date || 'comment'}-${commentIndex}`">
              <div class="contract-comment__meta">
                <span>
                  <v-icon small>mdi-clock-outline</v-icon>
                  {{ comment.date ? $moment(comment.date).format("DD.MM.YYYY, HH:mm") : "Datum unbekannt" }}
                </span>
                <v-chip v-if="comment.status" small outlined color="warning">{{ comment.status }}</v-chip>
              </div>
              <p v-if="comment.comment">{{ comment.comment }}</p>
              <p v-else class="contract-comment__empty">Kein zusätzlicher Kommentar hinterlegt.</p>
            </article>
          </div>

          <div v-else class="contract-comments-empty">
            <v-icon size="42">mdi-comment-text-outline</v-icon>
            <strong>Noch keine internen Kommentare</strong>
            <span>Für diesen Vertrag wurde bisher kein Kommentar hinterlegt.</span>
          </div>
        </v-card-text>

        <v-card-actions class="contract-comments-card__footer">
          <v-btn text class="text-none" @click="closeContractComments(false)">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <termination-dialog
      v-if="user"
      :dialog="terminationDialog"
      :user="user"
      :contract-id="selectedContractId"
      @close="closeTerminationDialog"></termination-dialog>
  </v-row>
</template>
<script>
import api from "@/api"
import anamneseBogenCreate from "../../../components/anamneseBogenCreate"
import MailTerminationWizard from "@/components/ai/mailTerminationWizard.vue"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import basicDialog from "@/components/tools/basicDialog"
import terminationDialog from "./userDashboardTerminationDialog.vue" // Pfad anpassen
export default {
  components: { basicDialog, userSearch, anamneseBogenCreate, terminationDialog, MailTerminationWizard },
  props: {
    user: {
      required: true,
    },
    isMobile: {
      required: true,
    },
    initialTacContractInfos: {
      type: Array,
      default: null,
    },
    tacContractsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showTerminationWizard: false,
      guestEntries: [],
      createNewGuestEntry: false,
      newGuestEntry: { user: null, date: this.$moment() },
      userService,
      activeIndexContract: 0,
      tempContract: null,
      showEditDialog: null,
      showAttachmentDialog: null,
      showPaymentDialog: null,
      commentDialogContract: null,
      tacContractsSwitch: false,
      imageSources: {},
      tab: null,
      tabs: [
        {
          text: "App Verträge",
        },
        {
          text: "TAC Verträge",
        },
      ],
      contracts: null,
      tacContractInfos: null,
      loadingTacContracts: false,
      pendingUpgrades: [],
      loading: false,
      existingDocs: null,
      loadingExistingDocs: false,
      showExistingGuestEntry: null,
      terminationDialog: false,
      selectedContractId: null,
    }
  },
  async created() {
    if (Array.isArray(this.initialTacContractInfos)) {
      this.applyTacContractInfos(this.initialTacContractInfos)
    } else {
      this.fetchTACContractInfos()
    }
    this.fetchContracts()
    this.fetchPendingUpgrades()

    if (this.user.guestEntries) {
      this.guestEntries = JSON.parse(JSON.stringify(this.user.guestEntries))
    }
  },
  methods: {
    // in methods:
    onTerminationCompleted() {
      this.showTerminationWizard = false
      this.refreshTacContracts()
      this.$toast.success("Verträge wurden aktualisiert")
    },
    closeTerminationDialog() {
      this.terminationDialog = false
      this.selectedContractId = null
    },
    openContractComments(contract) {
      this.commentDialogContract = contract
    },
    closeContractComments(isOpen) {
      if (!isOpen) this.commentDialogContract = null
    },
    getCommentCount(contract) {
      return Array.isArray(contract && contract.comments) ? contract.comments.length : 0
    },
    getContractTitle(contract) {
      const selection = contract && contract.membership ? contract.membership.selection : null
      return selection && selection.title ? selection.title : "Mitgliedschaft nicht hinterlegt"
    },
    getContractCategory(contract) {
      const selection = contract && contract.category ? contract.category.selection : null
      return selection && selection.title ? selection.title : "Kategorie nicht hinterlegt"
    },
    applyTacContractInfos(contracts) {
      this.tacContractInfos = [...(Array.isArray(contracts) ? contracts : [])].sort((a, b) => {
        if (a.CONTRACTSTATE === "Laufend" && b.CONTRACTSTATE !== "Laufend") return -1
        if (a.CONTRACTSTATE !== "Laufend" && b.CONTRACTSTATE === "Laufend") return 1
        return this.$moment(b.TERM_ENDDATE).valueOf() - this.$moment(a.TERM_ENDDATE).valueOf()
      })
    },
    async fetchPendingUpgrades() {
      if (!this.user?._id || !this.userService.hasRight("c_create_customer", "c_open_contracts")) {
        this.pendingUpgrades = []
        return
      }
      try {
        const upgrades = await api.getCustomerUpgrades(this.user._id)
        this.pendingUpgrades = Array.isArray(upgrades) ? upgrades : []
      } catch (error) {
        this.pendingUpgrades = []
      }
    },
    refreshTacContracts() {
      if (Array.isArray(this.initialTacContractInfos)) {
        this.$emit("refresh-tac-contracts")
      } else {
        this.fetchTACContractInfos()
      }
    },
    refreshExternalData() {
      this.fetchPendingUpgrades()
      if (!Array.isArray(this.initialTacContractInfos)) this.fetchTACContractInfos()
    },
    deletePremiumGuest() {
      if (confirm("Möchtest du die Einträge vom " + this.$moment(this.showExistingGuestEntry.date).format("DD.MM.YYYY") + " wirklich löschen?")) {
        api.updateUserGuestEntries({
          userId: this.user._id,
          guestEntries: this.guestEntries.filter((e) => e.date.toString() !== this.showExistingGuestEntry.date.toString()),
        })
        this.guestEntries = this.guestEntries.filter((e) => e.date.toString() !== this.showExistingGuestEntry.date.toString())
        this.showExistingGuestEntry = null

        this.$emit("refetchUser")
      }
    },
    openTerminationDialog(contract) {
      console.log("OPEN TERMINATION DIALOG", contract)
      // Wir brauchen die CONTRACTID des Hauptvertrags
      this.selectedContractId = contract.CONTRACTID
      this.terminationDialog = true
    },
    showGuestEntry(index) {
      this.showExistingGuestEntry = this.filteredGuestEntries[index - 1]
    },
    handleGuestSlot(index) {
      if (index <= this.filteredGuestEntries.length) {
        this.showGuestEntry(index)
        return
      }
      if (index === this.filteredGuestEntries.length + 1 && this.filteredGuestEntries.length < this.maxGuestEntries) {
        this.createNewGuestEntry = true
      }
    },
    viewGuestAnamnese() {
      const anamneseId = this.existingDocs?.medicalHistory?.[0]?._id
      if (!anamneseId || this.loadingExistingDocs) return
      this.$refs.anamneseBogenRef.setVisible(anamneseId, false)
    },
    async setGuestUser(id) {
      this.newGuestEntry.user = id
      this.fetchExistingGuestDocs(id)
    },
    async fetchExistingGuestDocs(id) {
      this.loadingExistingDocs = true
      this.existingDocs = await api.getCustomerDocuments(id)
      this.loadingExistingDocs = false
    },
    async updateUserGuestEntries() {
      this.guestEntries.push(this.newGuestEntry)
      await api.updateUserGuestEntries({ userId: this.user._id, guestEntries: this.guestEntries })

      this.$emit("refetchUser")
      this.createNewGuestEntry = false
      this.newGuestEntry = { user: null, date: this.$moment() }
    },
    async updateContractPayment(value, contractId, amount = 0, year) {
      let updateRequestData = {
        amount,
        value,
        year,
        contractIdSequenceId: contractId.toString(),
        userId: this.user._id,
      }
      try {
        let status = await api.updateTACContractPaidStatus(updateRequestData)
        console.log(status)
        this.$toast.success("Status aktualisiert")
        this.$emit("refetchUser")
      } catch (e) {
        this.$toast.error("Fehler beim aktualisieren")
      }
    },
    async fetchImageDataSource(imageIndex, contract) {
      let image = contract.customer.additionalImages[imageIndex]
      let src = image.src

      try {
        const response = await api.fetchContractImageData(src)
        contract.customer.additionalImages[imageIndex].dataUrl = response
        this.$forceUpdate()
      } catch (error) {
        this.$toast.error("Fehler beim laden des Bildes")
      }
    },

    deleteImageContract(index, contract) {
      contract.customer.additionalImages.splice(index, 1)
      this.$forceUpdate()
    },
    uploadImagesContract(files, contract) {
      for (let file of files) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const formData = new FormData()
          formData.append("file", file)

          api
            .uploadContractImage(formData)
            .then((response) => {
              const uploadedImageName = response.name

              if (!contract.customer.additionalImages) contract.customer.additionalImages = []
              contract.customer.additionalImages.push({
                title: "",
                src: uploadedImageName,
                dataUrl: e.target.result,
              })
              this.$forceUpdate()
            })
            .catch((error) => {
              console.log("Upload failed", error)
            })
        }
        reader.readAsDataURL(file)
      }
    },
    onFileSelected(event) {
      this.uploadStatus = ""
      this.selectedFile = event.target.files[0]
    },
    async checkImageSources(imagePaths) {
      this.imageSources = []
      for (let image of imagePaths) {
        if (!this.imageSources[image]) {
          this.imageSources[image] = await api.getContractImage(image)
        }
      }
      this.$forceUpdate()
    },
    async uploadPictureNew() {
      if (!this.selectedFile) {
        this.uploadStatus = "Please select an image to upload."
        return
      }

      const formData = new FormData()
      formData.append("file", this.selectedFile)

      let uploadImg = {
        art: "contract",
        formData: formData,
      }

      this.uploadStatus = "Uploading..."

      try {
        const data = await api.uploadImage(uploadImg)

        if (data.status) {
          let editingId = this.showAttachmentDialog.split("-")[0]
          let index = this.showAttachmentDialog.split("-")[1]
          index = parseInt(index)

          this.editing.contracts[index].customer.additionalImage.push(data.name)
          this.checkImageSources(this.editing.contracts[index].customer.additionalImage)
          this.uploadStatus = "Upload successful!"
        } else {
          this.uploadStatus = "Error uploading: " + data.message
        }
      } catch (error) {
        this.uploadStatus = `Error uploading: ${error.message}`
      } finally {
        this.$refs.fileInput.value = ""
        this.selectedFile = null
      }
    },
    uploadPicture(formdata, progress, success, failure, slim) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      let uploadimg = {}
      uploadimg.art = "contract"
      uploadimg.formData = formData

      api
        .uploadImage(uploadimg)
        .then((data) => {
          if (data.status) {
            //this.fieldEntries.customer.additionalImage.push(data.name)
            let editingId = this.showAttachmentDialog.split("-")[0]
            let index = this.showAttachmentDialog.split("-")[1]

            index = parseInt(index)

            this.editing.contracts[index].customer.additionalImage.push(data.name)
            this.$forceUpdate()
            success(data)
          }
        })
        .catch((error) => {
          //this.fieldEntries.customer.additionalImage.push(data.name)
          this.$forceUpdate()
          failure(error)
        })
    },
    async addFileToContract(index) {
      // delete dataUrl from this.editing.contracts[index].customer.additionalImages
      if (this.contracts[index].customer.additionalImages) {
        this.contracts[index].customer.additionalImages.forEach((e) => {
          delete e.dataUrl
        })
      }
      let newAdditionalImages = this.contracts[index].customer.additionalImages
      let status = await api.addAttachmentToContract({
        additionalImages: this.contracts[index].customer.additionalImage,
        newAdditionalImages,
        userId: this.user._id,
        index,
      })
      this.showAttachmentDialog = null
      this.$toast.success("Erfolgreich gespeichert")
    },
    async editPaymentContract(index, paymentOptions) {
      try {
        let status = await api
          .updatePaymentOptionsContract({ userId: this.user._id, index, paymentOptions })
          .catch((e) => this.$toast.error("Fehler beim speichern, bitte versuche es erneut"))

        this.$toast.success("Erfolgreich gespeichert")
        this.showPaymentDialog = null
        this.fetchContracts()
      } catch (e) {
        this.$toast.error("Fehler beim speichern")
      }
    },
    async fetchContracts() {
      try {
        let dataTemp = await api.getCustomerContracts(this.user._id)
        this.contracts = dataTemp
        this.$emit("loaded")
      } catch (e) {
        this.$toast.error("Fehler beim laden der Dokumente")
      }
    },
    async declineContract(contractIndex) {
      if (confirm("Soll der Vertrag wirklich abgelehnt werden?")) {
        try {
          let status = await api.declineContract({ userId: this.user._id, contractIndex })
          this.fetchContracts()
          this.$toast.success("Vertrag erfolgreich abgelehnt")
        } catch (e) {
          this.$toast.error("Vertrag konnte nicht abgelehnt werden")
        }
      }
    },
    printUserContract(index) {
      api.printUserContract(this.user._id, index, "Mitgliedschaftsvertrag")
    },

    mapContractState(state) {
      let states = {
        rejected: { text: "Abgelehnt", color: "red" },
        created: { text: "Offen", color: "orange" },
        temporary: { text: "Temporär", color: "orange" },
        accepted: { text: "Akzeptiert", color: "green" },
        deleted: { text: "Gelöscht", color: "red" },
      }
      return states[state] || { text: state || "Unbekannt", color: "grey" }
    },
    async fetchTACContractInfos() {
      this.loadingTacContracts = true
      try {
        const tacContractInfos = await api.fetchTACContractDetails(this.user._id)
        this.applyTacContractInfos(tacContractInfos)
      } catch (e) {
        this.tacContractInfos = []
        console.log("Fehler beim laden der TAC infos")
      } finally {
        this.loadingTacContracts = false
      }
    },
  },
  computed: {
    activeContractComments() {
      const comments = this.commentDialogContract && Array.isArray(this.commentDialogContract.comments) ? this.commentDialogContract.comments : []
      return [...comments].sort((a, b) => this.$moment(b.date).valueOf() - this.$moment(a.date).valueOf())
    },
    membershipLoading() {
      return this.tacContractsLoading || this.loadingTacContracts
    },
    currentTacMembership() {
      const now = this.$moment()
      return (
        this.runningTACContracts.find((contract) => {
          const type = String(contract?.CONTRACTTYP || "").trim()
          const group = String(contract?.GROUP_NAME || "").trim()
          const start = this.$moment(contract?.TERM_STARTDATE)
          const end = this.$moment(contract?.TERM_ENDDATE)
          const isMembership = type === "Hauptvertrag" || (type === "Bonusvertrag" && group !== "Zusatzpakete")
          return isMembership && start.isValid() && end.isValid() && now.isSameOrAfter(start, "day") && now.isSameOrBefore(end, "day")
        }) || null
      )
    },
    currentTacExtensions() {
      const current = this.currentTacMembership
      if (!current || current.CONTRACTID == null || current.RESSORTGROUPID == null) return []
      const candidates = (this.tacContractInfos || [])
        .filter((contract) => {
          const type = String(contract.CONTRACTTYP || "").trim()
          const term = String(contract.TERM_TYPE || "").trim()
          const group = String(contract.GROUP_NAME || "").trim()
          const isExtra = type === "Bonusvertrag" || term === "Bonusperiode" || contract.BONUSPERIODID != null || term === "Ruhezeitverlängerung"
          return (
            isExtra && type !== "Zusatzvertrag" && group !== "Zusatzpakete" &&
            contract.CONTRACTSTATE === "Laufend" &&
            String(contract.CONTRACTID) === String(current.CONTRACTID) &&
            String(contract.RESSORTGROUPID) === String(current.RESSORTGROUPID) &&
            this.$moment(contract.TERM_STARTDATE).isValid() && this.$moment(contract.TERM_ENDDATE).isValid() &&
            this.$moment(contract.TERM_ENDDATE).isSameOrAfter(this.$moment(contract.TERM_STARTDATE), "day")
          )
        })
        .sort((a, b) => this.$moment(a.TERM_STARTDATE).valueOf() - this.$moment(b.TERM_STARTDATE).valueOf())
      const periods = []
      let end = this.$moment(current.TERM_ENDDATE)
      for (const contract of candidates) {
        // Nur direkt anschliessende Zusatzzeiten, keine regulären Folgelaufzeiten.
        if (this.$moment(contract.TERM_STARTDATE).isSame(end.clone().add(1, "day"), "day")) {
          periods.push(contract)
          end = this.$moment(contract.TERM_ENDDATE)
        }
      }
      return periods
    },
    currentTacEnd() {
      const periods = this.currentTacExtensions
      return periods.length ? periods[periods.length - 1].TERM_ENDDATE : this.currentTacMembership?.TERM_ENDDATE
    },
    currentTacExtensionLabel() {
      const labels = []
      if (this.currentTacExtensions.some((period) => period.TERM_TYPE !== "Ruhezeitverlängerung")) labels.push("Bonuszeit")
      if (this.currentTacExtensions.some((period) => period.TERM_TYPE === "Ruhezeitverlängerung")) labels.push("Ruhezeitverlängerung")
      return labels.join(" und ")
    },
    currentTacPackages() {
      if (!this.currentTacMembership) return []
      const membershipStart = this.$moment(this.currentTacMembership.TERM_STARTDATE)
      const membershipEnd = this.$moment(this.currentTacMembership.TERM_ENDDATE)
      const matchingPackages = this.runningTACContracts.filter((contract) => {
        const type = String(contract?.CONTRACTTYP || "").trim()
        const group = String(contract?.GROUP_NAME || "").trim()
        const isPackage = type === "Zusatzvertrag" || (type === "Bonusvertrag" && group === "Zusatzpakete")
        if (!isPackage) return false

        const packageStart = this.$moment(contract?.TERM_STARTDATE)
        const packageEnd = this.$moment(contract?.TERM_ENDDATE)
        return (
          packageStart.isValid() && packageEnd.isValid() && packageStart.isSame(membershipStart, "day") && packageEnd.isSame(membershipEnd, "day")
        )
      })

      if (matchingPackages.length) return matchingPackages
      return this.runningTACContracts.filter((contract) => {
        const type = String(contract?.CONTRACTTYP || "").trim()
        const group = String(contract?.GROUP_NAME || "").trim()
        return type === "Zusatzvertrag" || (type === "Bonusvertrag" && group === "Zusatzpakete")
      })
    },
    currentTacPeriod() {
      if (!this.currentTacMembership) return ""
      return `${this.$moment(this.currentTacMembership.TERM_STARTDATE).format("DD.MM.YYYY")} – ${this.$moment(
        this.currentTacMembership.TERM_ENDDATE,
      ).format("DD.MM.YYYY")}`
    },
    currentTacDaysRemaining() {
      if (!this.currentTacMembership) return 0
      return Math.max(0, this.$moment(this.currentTacEnd).startOf("day").diff(this.$moment().startOf("day"), "days"))
    },
    currentTacCenter() {
      if (!this.currentTacMembership) return ""
      return (
        this.currentTacMembership.CENTER_NAME ||
        this.currentTacMembership.STUDIO_NAME ||
        this.currentTacMembership.CENTER ||
        this.currentTacMembership.STUDIO ||
        ""
      )
    },
    premiumContract() {
      return (
        this.runningTACContracts.find((contract) => {
          const contractName = String(contract && contract.NAME ? contract.NAME : "")
            .trim()
            .toLocaleLowerCase()
          return contractName.includes("premium")
        }) || null
      )
    },
    premiumGuestPeriod() {
      if (!this.premiumContract) return null

      const contractStart = this.$moment(this.premiumContract.TERM_STARTDATE).startOf("day")
      const contractEnd = this.$moment(this.premiumContract.TERM_ENDDATE).endOf("day")
      const today = this.$moment()
      if (!contractStart.isValid() || !contractEnd.isValid()) return null

      let periodStart = contractStart.clone()
      while (periodStart.clone().add(1, "year").isSameOrBefore(today, "day")) {
        periodStart.add(1, "year")
      }

      const yearlyEnd = periodStart.clone().add(1, "year").subtract(1, "day").endOf("day")
      const periodEnd = yearlyEnd.isBefore(contractEnd) ? yearlyEnd : contractEnd.clone()
      return { start: periodStart, end: periodEnd }
    },
    premiumGuestPeriodLabel() {
      if (!this.premiumGuestPeriod) return ""
      return `${this.premiumGuestPeriod.start.format("DD.MM.YY")}–${this.premiumGuestPeriod.end.format("DD.MM.YY")}`
    },
    wizardUserData() {
      return {
        fullName: this.user.vorname + " " + this.user.nachname,
        firstName: this.user.vorname,
        email: this.user.email,
        _id: this.user._id, // Roody-ID für den Wizard
      }
    },
    maxGuestEntries() {
      return this.premiumContract ? 10 : 0
    },
    showPremiumDialog: {
      get() {
        return Boolean(this.showExistingGuestEntry)
      },
      set(isOpen) {
        if (!isOpen) this.showExistingGuestEntry = null
      },
    },
    filteredGuestEntries() {
      if (!this.premiumGuestPeriod) return []
      return this.guestEntries
        .filter((entry) => {
          const entryDate = this.$moment(entry && entry.date)
          return (
            entryDate.isValid() &&
            entryDate.isSameOrAfter(this.premiumGuestPeriod.start, "day") &&
            entryDate.isSameOrBefore(this.premiumGuestPeriod.end, "day")
          )
        })
        .sort((a, b) => this.$moment(a.date).valueOf() - this.$moment(b.date).valueOf())
    },
    runningTACContracts() {
      if (!this.tacContractInfos) return []

      return this.tacContractInfos.filter(
        (contract) =>
          contract.CONTRACTSTATE === "Laufend" &&
          this.$moment().isSameOrBefore(this.$moment(contract.TERM_ENDDATE), "day") &&
          this.$moment().isSameOrAfter(this.$moment(contract.TERM_STARTDATE), "day"),
      )
    },
    oldTACContracts() {
      if (!this.tacContractInfos) return []
      return this.tacContractInfos.filter(
        (contract) =>
          !(
            contract.CONTRACTSTATE === "Laufend" &&
            this.$moment().isSameOrBefore(this.$moment(contract.TERM_ENDDATE), "day") &&
            this.$moment().isSameOrAfter(this.$moment(contract.TERM_STARTDATE), "day")
          ),
      )
    },
    isLocal() {
      return location.host.includes("local")
    },
  },
  watch: {
    initialTacContractInfos: {
      deep: true,
      handler(contracts) {
        if (Array.isArray(contracts)) this.applyTacContractInfos(contracts)
      },
    },
    showAttachmentDialog: function (val) {
      if (val) this.checkImageSources(this.contracts[this.showAttachmentDialog.split("-")[1]].customer.additionalImage)
    },
    showPaymentDialog: async function (val) {
      if (val) {
        let index = val.replace("-contractPaymentIndex", "")

        let contractId = this.contracts[parseInt(index)].contractId
        console.log(contractId)
        let contract = await api.fetchContract(contractId)
        this.tempContract = contract
      } else {
        this.tempContract = null
      }
    },
  },
}
</script>
<style scoped lang="scss">
.contracts-dashboard {
  padding: 4px 16px 20px;
  color: #f2f5f3;

  ::v-deep .v-tabs-items {
    background: transparent !important;
  }

  ::v-deep .contract-tabs .v-tabs-bar {
    height: 54px;
    padding: 5px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 15px;
    background: rgba(0, 0, 0, 0.16) !important;
  }

  ::v-deep .contract-tabs .v-slide-group__content {
    gap: 5px;
  }

  ::v-deep .contract-tabs .v-tab {
    min-width: 150px;
    border-radius: 10px;
    color: #a7ada9 !important;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: none;
  }

  ::v-deep .contract-tabs .v-tab--active {
    color: #80d7a1 !important;
    background: rgba(0, 150, 64, 0.14);
  }

  ::v-deep .contract-tabs .v-tabs-slider-wrapper {
    display: none;
  }

  ::v-deep .v-alert {
    border-radius: 14px;
    font-size: 12px;
  }

  ::v-deep .v-alert .v-icon {
    color: currentColor !important;
  }

  ::v-deep .v-input__slot {
    border-radius: 11px !important;
    background: rgba(0, 0, 0, 0.13) !important;
  }

  ::v-deep .v-label,
  ::v-deep input,
  ::v-deep textarea,
  ::v-deep .v-select__selection,
  ::v-deep .v-input--selection-controls__input + .v-label {
    color: #d7dcd8 !important;
    font-size: 12px !important;
  }

  ::v-deep .v-btn .v-icon {
    color: currentColor !important;
  }
}

.current-membership {
  margin: 12px 0 6px;
  padding: 0 !important;
  overflow: hidden;
  border: 1px solid rgba(96, 213, 140, 0.22);
  border-radius: 20px;
  background: radial-gradient(circle at 93% 0%, rgba(0, 167, 74, 0.19), transparent 37%),
    linear-gradient(145deg, rgba(20, 42, 31, 0.94), rgba(16, 21, 18, 0.96));
}

.current-membership__main {
  display: grid;
  min-height: 124px;
  align-items: center;
  gap: 16px;
  padding: 20px;
  grid-template-columns: 58px minmax(0, 1fr) auto auto;
}

.current-membership__icon {
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(96, 221, 143, 0.22);
  border-radius: 17px;
  color: #6ce099;
  background: rgba(0, 150, 64, 0.14);
}

.current-membership__icon .v-icon {
  color: inherit !important;
}

.current-membership__icon--muted {
  border-color: rgba(148, 163, 184, 0.19);
  color: #9ba6ae;
  background: rgba(148, 163, 184, 0.09);
}

.current-membership__identity {
  min-width: 0;
}

.current-membership__eyebrow,
.current-membership__detail-label {
  color: #65d58f;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.1em;
}

.current-membership__identity h3,
.current-membership__empty h3 {
  margin: 4px 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 22px;
  letter-spacing: -0.025em;
  line-height: 1.25;
  text-overflow: ellipsis;
}

.current-membership__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 14px;
  margin-top: 8px;
}

.current-membership__meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #aeb9b2;
  font-size: 12px;
}

.current-membership__meta .v-icon {
  color: #9eaaa2 !important;
}

.current-membership__status {
  display: flex;
  min-width: 104px;
  flex-direction: column;
  padding: 10px 15px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  text-align: right;
}

.current-membership__status strong {
  color: #ffffff;
  font-size: 24px;
  line-height: 1;
}

.current-membership__status span {
  margin-top: 4px;
  color: #98a39c;
  font-size: 12px;
}

.current-membership__upgrade {
  min-height: 44px;
  border-radius: 12px;
}

.current-membership__details {
  display: grid;
  align-items: center;
  gap: 14px;
  padding: 14px 20px 17px 94px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(0, 0, 0, 0.1);
  grid-template-columns: minmax(0, 1fr) auto;
}

.current-membership__total {
  margin: 0.5rem 0 0;
  color: #bde9cd;
  font-size: 0.875rem;
  line-height: 1.5;
}

.current-membership__extensions {
  min-width: 0;
  grid-column: 1 / -1;
}

.current-membership__extensions ul {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  margin: 0.5rem 0 0;
  list-style: none;
}

.current-membership__extensions li {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  color: #bde9cd;
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.current-membership__packages {
  min-width: 0;
}

.current-membership__package-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 8px;
}

.current-membership__package {
  display: inline-flex;
  min-height: 29px;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border: 1px solid rgba(98, 217, 143, 0.22);
  border-radius: 999px;
  color: #bde9cd;
  background: rgba(0, 150, 64, 0.1);
  font-size: 12px;
  font-weight: 700;
}

.current-membership__package .v-icon {
  color: #70d997 !important;
}

.current-membership__empty-package {
  display: block;
  margin-top: 7px;
  color: #89938c;
  font-size: 12px;
}

.current-membership__pending {
  display: grid;
  min-width: 210px;
  min-height: 58px;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border: 1px solid rgba(245, 158, 11, 0.23);
  border-radius: 13px;
  color: #f0c77c;
  background: rgba(245, 158, 11, 0.08);
  cursor: pointer;
  font: inherit;
  grid-template-columns: 34px minmax(0, 1fr) 22px;
  text-align: left;
}

.current-membership__pending > span:first-child {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(245, 158, 11, 0.11);
}

.current-membership__pending > span:nth-child(2) {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.current-membership__pending strong,
.current-membership__pending small {
  font-size: 12px;
}

.current-membership__pending small {
  color: #a99d87;
}

.current-membership__pending .v-icon {
  color: inherit !important;
}

.current-membership__loading,
.current-membership__empty {
  display: flex;
  min-height: 128px;
  align-items: center;
  gap: 17px;
  padding: 22px;
}

.current-membership__loading > div,
.current-membership__empty > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.current-membership__loading strong {
  color: #ffffff;
  font-size: 15px;
}

.current-membership__loading span,
.current-membership__empty p {
  margin: 4px 0 0;
  color: #9aa49d;
  font-size: 12px;
}

.premium-guests {
  margin: 12px 0 8px;
  padding: 18px 20px !important;
  overflow: hidden;
  border: 1px solid rgba(70, 214, 125, 0.22);
  border-radius: 18px;
  background: radial-gradient(circle at 96% 0%, rgba(0, 184, 83, 0.18), transparent 42%),
    linear-gradient(145deg, rgba(20, 47, 34, 0.9), rgba(17, 22, 19, 0.92));
}

.premium-guests__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 15px;
}

.premium-guests__header h3 {
  margin: 2px 0 3px;
  color: #ffffff;
  font-size: 19px;
  line-height: 1.2;
}

.premium-guests__header p {
  margin: 0;
  color: #aebbb3;
  font-size: 12px;
}

.premium-guests__header > .v-icon {
  padding: 11px;
  border: 1px solid rgba(91, 224, 141, 0.22);
  border-radius: 13px;
  color: #64df94 !important;
  background: rgba(0, 150, 64, 0.12);
}

.premium-guests__eyebrow {
  color: #55d786;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.08em;
}

.premium-guests__slots {
  gap: 8px;
}

.premium-guests__slots ::v-deep .v-chip {
  min-width: 38px;
  justify-content: center;
  margin: 0 !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
}

.contract-card {
  min-height: 225px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09) !important;
  border-radius: 18px !important;
  background: radial-gradient(circle at 95% 0%, rgba(0, 150, 64, 0.12), transparent 38%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.04), rgba(0, 0, 0, 0.17)) !important;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.14) !important;

  ::v-deep .v-card__title {
    color: #ffffff !important;
    font-size: 18px !important;
    letter-spacing: -0.02em;
  }

  ::v-deep .v-card__subtitle,
  ::v-deep .v-card__text {
    color: #aeb5b0 !important;
    font-size: 12px !important;
  }

  ::v-deep .v-chip {
    border-radius: 999px !important;
    font-size: 12px !important;
    font-weight: 750;
  }

  ::v-deep .v-card__actions {
    margin-top: auto;
    padding: 10px 14px 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  ::v-deep .v-card__actions .v-btn {
    margin: 3px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.045);
  }

  ::v-deep .v-card__actions .v-btn .v-icon {
    color: #cfd6d1 !important;
  }
}

.contract-card--roody {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  text-align: left;
}

.contract-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
}

.contract-card__heading {
  min-width: 0;
}

.contract-card__eyebrow,
.contract-card__section-label,
.contract-comments-card__eyebrow {
  color: #67d993;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.contract-card__heading h3 {
  margin: 3px 0 4px;
  overflow-wrap: anywhere;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.25;
}

.contract-card__category {
  color: #aeb8b1;
  font-size: 0.875rem;
  line-height: 1.4;
}

.contract-card--roody .contract-status-chip {
  flex-shrink: 0;
  margin: 0;
}

.contract-card__meta {
  display: grid;
  gap: 10px;
  padding: 0 18px 17px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.contract-card__meta > div {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 9px;
  padding: 11px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.13);
}

.contract-card__meta .v-icon {
  margin-top: 2px;
  color: #7fd8a0 !important;
}

.contract-card__meta span {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.contract-card__meta small {
  color: #89958d;
  font-size: 0.75rem;
}

.contract-card__meta strong {
  color: #e9eeeb;
  font-size: 0.875rem;
  line-height: 1.35;
}

.contract-card__notice {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 18px 15px;
  padding: 10px 12px;
  border: 1px solid rgba(59, 130, 246, 0.22);
  border-radius: 10px;
  color: #bfd8fa;
  background: rgba(59, 130, 246, 0.08);
  font-size: 0.875rem;
}

.contract-card__notice .v-icon {
  color: #8abcf5 !important;
}

.contract-card__packages {
  padding: 0 18px 18px;
}

.contract-card__package-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 8px;
}

.contract-card__package-list ::v-deep .v-chip {
  margin: 0;
  color: #c8ead4 !important;
  border-color: rgba(103, 217, 147, 0.28) !important;
  background: rgba(0, 150, 64, 0.08) !important;
}

.contract-card__empty {
  display: block;
  margin-top: 7px;
  color: #8f9992;
  font-size: 0.875rem;
}

.contract-card--roody .contract-card__actions {
  display: block;
  width: 100%;
  margin-top: auto;
  padding: 14px 18px 18px;
}

.contract-card__action-list {
  display: grid;
  gap: 8px;
  margin-top: 9px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.contract-card__action-list ::v-deep .v-btn {
  width: 100%;
  min-height: 40px;
  height: auto !important;
  justify-content: flex-start;
  margin: 0 !important;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: normal;
}

.contract-card__action-list ::v-deep .v-btn .v-icon {
  color: currentColor !important;
}

.contract-action__count {
  display: inline-flex;
  min-width: 23px;
  min-height: 23px;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.75rem;
  font-weight: 800;
}

.contract-comments-card {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  background: radial-gradient(circle at 95% 0%, rgba(0, 150, 64, 0.16), transparent 38%), #171c19 !important;
}

.contract-comments-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.contract-comments-card__header h3 {
  margin: 3px 0;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.25;
}

.contract-comments-card__header p {
  margin: 0;
  color: #aab5ad;
  font-size: 0.875rem;
}

.contract-comments-card__content {
  padding: 18px !important;
}

.contract-comments-list {
  display: grid;
  gap: 11px;
}

.contract-comments-list article {
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.16);
}

.contract-comment__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 9px;
}

.contract-comment__meta > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #9ca8a0;
  font-size: 0.875rem;
}

.contract-comment__meta .v-icon {
  color: #78d59a !important;
}

.contract-comment__meta ::v-deep .v-chip {
  margin: 0;
  font-size: 0.75rem;
}

.contract-comments-list p {
  margin: 0;
  overflow-wrap: anywhere;
  color: #f1f4f2;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-wrap;
}

.contract-comments-list .contract-comment__empty {
  color: #929d96;
  font-size: 0.875rem;
}

.contract-comments-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  color: #929d96;
  text-align: center;
}

.contract-comments-empty .v-icon {
  margin-bottom: 4px;
  color: #708078 !important;
}

.contract-comments-empty strong {
  color: #dce2de;
  font-size: 1rem;
}

.contract-comments-empty span {
  max-width: 360px;
  font-size: 0.875rem;
}

.contract-comments-card__footer {
  justify-content: flex-end;
  padding: 11px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.contract-status-chip {
  margin-top: 14px;
  margin-bottom: 5px;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.2);
}

.contract-card--expired {
  opacity: 0.82;
  background: radial-gradient(circle at 95% 0%, rgba(148, 163, 184, 0.09), transparent 38%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.025), rgba(0, 0, 0, 0.2)) !important;
}

.clickable {
  cursor: pointer;
}

@media (max-width: 700px) {
  .contracts-dashboard {
    padding: 4px 3px 14px;
  }

  .contract-card {
    margin-right: 4px !important;
    margin-left: 4px !important;
  }

  .contract-card__header,
  .contract-comments-card__header {
    padding: 16px;
  }

  .contract-card__meta {
    padding: 0 16px 15px;
    grid-template-columns: 1fr;
  }

  .contract-card__notice {
    margin-right: 16px;
    margin-left: 16px;
  }

  .contract-card__packages {
    padding-right: 16px;
    padding-left: 16px;
  }

  .contract-card--roody .contract-card__actions {
    padding-right: 16px;
    padding-left: 16px;
  }

  .contract-comments-card {
    min-height: 100%;
    border: 0;
    border-radius: 0;
  }

  .contracts-dashboard ::v-deep .contract-tabs .v-tab {
    min-width: 118px;
  }

  .current-membership__main {
    align-items: flex-start;
    padding: 17px;
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .current-membership__icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
  }

  .current-membership__status {
    align-items: flex-start;
    padding: 3px 0;
    border-right: 0;
    grid-column: 2;
    text-align: left;
  }

  .current-membership__upgrade {
    width: 100%;
    grid-column: 1 / -1;
  }

  .current-membership__details {
    padding: 15px 17px 17px;
    grid-template-columns: 1fr;
  }

  .current-membership__pending {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 430px) {
  .contract-card__action-list {
    grid-template-columns: 1fr;
  }

  .contract-comment__meta {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
